import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, isSuperAdmin } from "@/lib/auth";
import { toast } from "sonner";
import { InlineDeleteButton } from "@/components/InlineDeleteButton";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Database, Plus, RefreshCw, Search, ShieldAlert, Save, Trash2 } from "lucide-react";

// ---------------------------------------------------------------------------
// MP12 Module 5D — Metadata Registry.
// Read visible to all internal roles; edits restricted to super_admin. Creating
// a registry entry does NOT rewrite any existing form or DB field. The registry
// is governance-and-visibility first.
// ---------------------------------------------------------------------------

const BLOCKER_STYLE = {
  NON_BLOCKER:         { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
  BLOCKER_APPROVED:    { bg: "hsl(210 45% 94%)", fg: "hsl(210 55% 30%)", border: "hsl(210 45% 74%)" },
  BLOCKER_UNJUSTIFIED: { bg: "hsl(0 55% 94%)",   fg: "hsl(0 65% 30%)",   border: "hsl(0 55% 72%)" },
  EXCEPTION_ACTIVE:    { bg: "hsl(40 55% 94%)",  fg: "hsl(35 60% 28%)",  border: "hsl(40 55% 76%)" },
};

function BlockerBadge({ value }) {
  const st = BLOCKER_STYLE[value] || BLOCKER_STYLE.NON_BLOCKER;
  return <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ background: st.bg, color: st.fg, borderColor: st.border }}>{value || "NON_BLOCKER"}</Badge>;
}

function RegistryEditor({ entry, vocab, onSaved, canEdit }) {
  const [row, setRow] = useState(entry);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRow(entry); }, [entry]);

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const r = await api.patch(`/metadata-registry/${entry.id}`, {
        field_name: row.field_name, module_location: row.module_location, purpose: row.purpose,
        requiredness: row.requiredness, lifecycle_state: row.lifecycle_state,
        blocker_state: row.blocker_state, blocker_reason: row.blocker_reason,
        rationale_category: row.rationale_category, named_rationale: row.named_rationale,
        owner_email: row.owner_email, owner_name: row.owner_name,
        last_reviewed_at: row.last_reviewed_at, usage_count: row.usage_count,
        usage_signal_notes: row.usage_signal_notes, notes: row.notes,
      });
      onSaved?.(r.data);
      toast.success("Saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid={`mr-editor-${entry.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><Label>Field name</Label><Input value={row.field_name || ""} disabled={!canEdit} onChange={(e)=>setRow({...row, field_name: e.target.value})} /></div>
        <div><Label>Module / location</Label><Input value={row.module_location || ""} disabled={!canEdit} onChange={(e)=>setRow({...row, module_location: e.target.value})} /></div>
        <div><Label>Owner (email)</Label><Input value={row.owner_email || ""} disabled={!canEdit} onChange={(e)=>setRow({...row, owner_email: e.target.value})} /></div>
        <div className="md:col-span-3"><Label>Purpose</Label><Textarea rows={2} value={row.purpose || ""} disabled={!canEdit} onChange={(e)=>setRow({...row, purpose: e.target.value})} /></div>
        <div>
          <Label>Requiredness</Label>
          <Select value={row.requiredness} disabled={!canEdit} onValueChange={(v)=>setRow({...row, requiredness: v})}>
            <SelectTrigger data-testid="mr-requiredness"><SelectValue /></SelectTrigger>
            <SelectContent>{(vocab.requiredness || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Lifecycle</Label>
          <Select value={row.lifecycle_state} disabled={!canEdit} onValueChange={(v)=>setRow({...row, lifecycle_state: v})}>
            <SelectTrigger data-testid="mr-lifecycle"><SelectValue /></SelectTrigger>
            <SelectContent>{(vocab.lifecycle_states || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Blocker state</Label>
          <Select value={row.blocker_state} disabled={!canEdit} onValueChange={(v)=>setRow({...row, blocker_state: v})}>
            <SelectTrigger data-testid="mr-blocker-state"><SelectValue /></SelectTrigger>
            <SelectContent>{(vocab.blocker_states || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Rationale category</Label>
          <Select value={row.rationale_category} disabled={!canEdit} onValueChange={(v)=>setRow({...row, rationale_category: v})}>
            <SelectTrigger data-testid="mr-rationale-cat"><SelectValue /></SelectTrigger>
            <SelectContent>{(vocab.rationale_categories || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2"><Label>Named rationale</Label><Input value={row.named_rationale || ""} disabled={!canEdit} onChange={(e)=>setRow({...row, named_rationale: e.target.value})} /></div>
        <div className="md:col-span-3"><Label>Blocker reason</Label><Textarea rows={2} value={row.blocker_reason || ""} disabled={!canEdit} onChange={(e)=>setRow({...row, blocker_reason: e.target.value})} /></div>
        <div><Label>Last reviewed</Label><Input value={row.last_reviewed_at || ""} disabled={!canEdit} onChange={(e)=>setRow({...row, last_reviewed_at: e.target.value})} placeholder="YYYY-MM-DD" /></div>
        <div><Label>Usage count</Label><Input type="number" value={row.usage_count ?? 0} disabled={!canEdit} onChange={(e)=>setRow({...row, usage_count: parseInt(e.target.value, 10) || 0})} /></div>
        <div><Label>Owner name</Label><Input value={row.owner_name || ""} disabled={!canEdit} onChange={(e)=>setRow({...row, owner_name: e.target.value})} /></div>
        <div className="md:col-span-3"><Label>Notes</Label><Textarea rows={2} value={row.notes || ""} disabled={!canEdit} onChange={(e)=>setRow({...row, notes: e.target.value})} /></div>
      </div>
      {canEdit && <Button onClick={save} disabled={saving} className="gap-2" data-testid="mr-save"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</Button>}
      {row.blocker_state === "BLOCKER_APPROVED" && (
        <div className="text-[11px] text-muted-foreground flex items-center gap-2">
          <ShieldAlert className="w-3.5 h-3.5" /> BLOCKER_APPROVED requires a named rationale in a critical category (LEGAL / RIGHTS / ACCOUNTING / DELIVERY / COMPLIANCE). Server enforces this.
        </div>
      )}
    </div>
  );
}

function CreateRegistryDialog({ vocab, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    field_name: "", module_location: "", purpose: "",
    requiredness: "OPTIONAL", lifecycle_state: "ACTIVE",
    blocker_state: "NON_BLOCKER", rationale_category: "OPERATIONAL",
    named_rationale: "", owner_email: "",
  });
  const submit = async () => {
    if (!form.field_name.trim()) { toast.error("Field name required."); return; }
    setSaving(true);
    try {
      const r = await api.post("/metadata-registry", form);
      onCreated?.(r.data);
      toast.success("Registry entry created.");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed.");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="mr-new" className="gap-2"><Plus className="w-4 h-4" /> New entry</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New Metadata Registry Entry</DialogTitle>
          <DialogDescription>Creating an entry does NOT rewrite any existing form or DB field. Governance and visibility first.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Field name</Label><Input value={form.field_name} onChange={(e)=>setForm({...form, field_name: e.target.value})} data-testid="mr-new-field" /></div>
          <div><Label>Module / location</Label><Input value={form.module_location} onChange={(e)=>setForm({...form, module_location: e.target.value})} placeholder="tracks, sync_review_files…" /></div>
          <div className="col-span-2"><Label>Purpose</Label><Textarea rows={2} value={form.purpose} onChange={(e)=>setForm({...form, purpose: e.target.value})} /></div>
          <div>
            <Label>Requiredness</Label>
            <Select value={form.requiredness} onValueChange={(v)=>setForm({...form, requiredness: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{(vocab.requiredness || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Lifecycle</Label>
            <Select value={form.lifecycle_state} onValueChange={(v)=>setForm({...form, lifecycle_state: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{(vocab.lifecycle_states || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Blocker state</Label>
            <Select value={form.blocker_state} onValueChange={(v)=>setForm({...form, blocker_state: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{(vocab.blocker_states || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Rationale category</Label>
            <Select value={form.rationale_category} onValueChange={(v)=>setForm({...form, rationale_category: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{(vocab.rationale_categories || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Named rationale (required if BLOCKER_APPROVED)</Label><Input value={form.named_rationale} onChange={(e)=>setForm({...form, named_rationale: e.target.value})} /></div>
          <div className="col-span-2"><Label>Owner email</Label><Input value={form.owner_email} onChange={(e)=>setForm({...form, owner_email: e.target.value})} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="mr-new-save">{saving ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MetadataRegistryDesk() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [requiredness, setRequiredness] = useState("");
  const [lifecycle, setLifecycle] = useState("");
  const [vocab, setVocab] = useState({});
  const [expanded, setExpanded] = useState({});
  const canEdit = isSuperAdmin(user);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (q.trim()) params.q = q.trim();
      if (requiredness) params.requiredness = requiredness;
      if (lifecycle) params.lifecycle_state = lifecycle;
      const r = await api.get("/metadata-registry", { params });
      setRows(r.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed.");
    } finally { setLoading(false); }
  }, [q, requiredness, lifecycle]);

  useEffect(() => {
    api.get("/metadata-registry/meta/vocab").then((r)=>setVocab(r.data || {})).catch(()=>{});
    load();
  }, [load]);

  const remove = async (id) => {
    if (!isSuperAdmin(user)) return;
    if (!window.confirm("Delete this metadata registry entry?")) return;
    try { await api.delete(`/metadata-registry/${id}`); setRows((rs)=>rs.filter((r)=>r.id !== id)); toast.success("Deleted."); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed."); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="metadata-registry-desk">
      <div className="flex flex-wrap items-center gap-3">
        <Database className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Metadata Registry</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Governed catalogue of every material field. Requiredness, lifecycle, blocker state, and named rationale for any rule that blocks progress. Read-visible to internal roles. Edits restricted to Super Admin.
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">Internal-only</Badge>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="mr-refresh"><RefreshCw className="w-4 h-4" /> Refresh</Button>
        {canEdit && <CreateRegistryDialog vocab={vocab} onCreated={(x)=>setRows((rs)=>[x, ...rs])} />}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" /> Filters</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><Label>Search</Label><Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="field name, module, purpose…" data-testid="mr-filter-q" /></div>
            <div>
              <Label>Requiredness</Label>
              <Select value={requiredness || "__any__"} onValueChange={(v)=>setRequiredness(v === "__any__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any</SelectItem>
                  {(vocab.requiredness || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Lifecycle</Label>
              <Select value={lifecycle || "__any__"} onValueChange={(v)=>setLifecycle(v === "__any__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any</SelectItem>
                  {(vocab.lifecycle_states || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end"><Button className="w-full" onClick={load} data-testid="mr-filter-apply">Apply</Button></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Registry entries ({rows.length})</CardTitle>
          <CardDescription>Operationally critical entries are marked BLOCKER_APPROVED with a named rationale. Non-critical entries cannot block revenue on their own.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
           rows.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="mr-empty">No metadata registry entries recorded.</p> :
           <div className="space-y-2">
             {rows.map((r) => (
               <div key={r.id} className="border border-border rounded-md" data-testid={`mr-row-${r.id}`}>
                 <button
                   type="button"
                   className="w-full text-left px-4 py-3 hover:bg-accent flex items-center gap-2 flex-wrap"
                   onClick={()=>setExpanded({ ...expanded, [r.id]: !expanded[r.id] })}
                 >
                   <span className="font-mono text-xs opacity-70">{r.module_location || "—"}</span>
                   <span className="font-semibold text-sm">{r.field_name}</span>
                   <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{r.requiredness}</Badge>
                   <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{r.lifecycle_state}</Badge>
                   <BlockerBadge value={r.blocker_state} />
                   {r.blocker_state === "BLOCKER_APPROVED" && r.rationale_category && (
                     <span className="text-xs opacity-70">· {r.rationale_category}: {r.named_rationale}</span>
                   )}
                 </button>
                 {expanded[r.id] && (
                   <div className="border-t border-border px-4 py-3">
                     <RegistryEditor entry={r} vocab={vocab} canEdit={canEdit} onSaved={(x)=>setRows((rs)=>rs.map((row)=>row.id===x.id ? x : row))} />
                     {canEdit && (
                       <div className="mt-3">
                         <InlineDeleteButton
                        endpoint={`/metadata-registry/${r.id}`}
                        recordLabel="Metadata Registry entry"
                        recordId={r.id}
                        isSuperAdmin={isSuperAdmin(user)}
                        variant="outline"
                        onDeleted={(id) => setRows((rs) => rs.filter((x) => x.id !== id))}
                        testid={`mr-delete-${r.id}`}
                      />
                       </div>
                     )}
                   </div>
                 )}
               </div>
             ))}
           </div>
          }
        </CardContent>
      </Card>
    </div>
  );
}
